import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EvSimulatorSteps, evSimulatorStepsOrder } from '../../models/simulator';
import { StepManager } from '../../../../shared/IFE/step/step-manager/step-manager';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from '../../../../shared/modules/analytics/services/analytics.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { fadeInOut } from 'src/app/shared/utils/animations/fade-animation';
import { Simulation } from '../../../../shared/modules/evc-api/models/simulation';
import { take } from 'rxjs/operators';
import { EvcApiService } from '../../../../shared/modules/evc-api/services/evc-api.service';
import { ProgressBarType } from 'src/app/shared/IFE/components/progress-bar/models/progress-bar-type';
import { ToastrService } from 'ngx-toastr';
import { UtilsService } from '../../../../shared/utils/utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PartnerService } from 'src/app/modules/evc-quote/services/partner/partner.service';
import { PartnerUI } from '@app/shared/modules/evc-api/models/partner-ui.interface';

@Component({
  selector: 'app-ev-simulator',
  templateUrl: './ev-simulator.component.html',
  styleUrls: ['./ev-simulator.component.scss'],
  animations: [fadeInOut],
})
export class EvSimulatorComponent extends StepManager implements OnInit {
  readonly evSimulatorSteps = EvSimulatorSteps;
  readonly evSimulatorStepsOrder = evSimulatorStepsOrder;
  readonly progressBarType = ProgressBarType.bullets;
  public control: UntypedFormGroup;

  public simulation: Simulation;
  public partner: PartnerUI;

  constructor(
    location: Location,
    router: Router,
    title: Title,
    translate: TranslateService,
    private fb: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private partnerService: PartnerService,
    private api: EvcApiService,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {
    super(location, router, title, translate);
    // Set the title string.
    this.setTitle('modules.evSimulator.general.title');

    // Since all steps are defined in the routing. A 404 will be displayed when urlStep is not valid.
    this.activeStep = this.router.url.split('/').pop();

    // Check if we have a Quote id in url and retrieve the data.
    if (this.route.snapshot.queryParams.simId) {
      this.api.getSimulation(this.route.snapshot.queryParams.simId).subscribe(
        (simulation) => {
          this.simulation = simulation;
          this.goToStep(EvSimulatorSteps.personalCalculation);
        },
        (error: HttpErrorResponse) => {
          // No param or simulation go to the first step.
          this.goToStep(EvSimulatorSteps.existingClient);

          if (error.status !== 404) {
            throw error;
          }
        }
      );
    } else {
      this.goToStep(EvSimulatorSteps.existingClient);
    }

    this.partnerService.restoredPartner$.subscribe((partner: PartnerUI) => {
      if (partner) {
        this.partner = partner;
      }
    });
  }

  ngOnInit(): void {
    this.analytics();
    this.buildForm();
  }

  buildForm() {
    this.control = this.fb.group({
      existingClient: [],
      houseInfo: [],
      chargingStationPlacement: [],
      chargingStationDistance: [],
      electricPhase: [],
      contactInfo: [],
    });

    this.control.valueChanges.subscribe((values) => {
      this.valueAnalytics(values);
    });
  }

  public submit() {
    const simulation: Simulation = {
      houseType: this.control.get('houseInfo').value.houseType,
      ...(this.control.get('houseInfo').value.parkingSpot
        ? { parking: this.control.get('houseInfo').value.parkingSpot }
        : {}),
      placement: this.control.get('chargingStationPlacement').value,
      distance: this.control.get('chargingStationDistance').value,
      phase: this.control.get('electricPhase').value,
      email: this.control.get('contactInfo').value.email,
      lang: this.translate.currentLang.toLowerCase(),
      ...(this.partner ? { partner: this.partner.name } : {}),
      ...(this.partner && this.partner.resellerCode ? { resellerCode: this.partner.resellerCode } : {}),
    };

    if (this.control.get('contactInfo').value.newsletter) {
      // Hardcoded newsletter code for EV Mailing list.
      simulation.newsletters = ['MKT_EV'];
    }

    if (this.control.get('contactInfo').value.gdpr) {
      // GDPR compliance
      simulation.gdpr = true;
    }

    // Todo check for loading animation if needed.
    this.api
      .simulate(simulation)
      .pipe(take(1))
      .subscribe(
        (simulatedData) => {
          this.simulation = simulatedData;
          this.goToStep(EvSimulatorSteps.personalCalculation);
        },
        (error) => {
          this.toastr.error(this.translate.instant('errorMessages.serverError'));
          throw error;
        }
      );
  }

  private analytics() {
    this.analyticsService.push({
      event: 'evcSimulator-Pageview',
      page: {
        phase: 'sales',
        category: 'evc-simulator',
        branch: 'charging-station',
        simType: 'light',
        simulationConversion: 0,
        language: this.translate.currentLang,
      },
    });
  }

  private valueAnalytics(values) {
    //Deep Clone the values.
    const stringValues = JSON.stringify(values);
    const analyticsValues = JSON.parse(stringValues);

    UtilsService.prune(analyticsValues);

    this.analyticsService.push({
      event: 'evcSimulator-Interaction',
      eventAction: 'valueChange',
      page: {
        evcSimulator: analyticsValues,
      },
    });
  }
}
